import { Button, Stack, Text, useViewport } from "@bookingcom/bui-react";

import React, { useCallback, useContext, useEffect, useMemo } from "react";

import NotAvailableIllustration from "../../../assets/illustrations/not-available.svg";
import useUserAgent from "../../../hooks/useUserAgent";
import { actions as AriaLiveActions } from "../../../store/ariaLive/actions";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useActions, useStore } from "../../../store";

import useModalDialog from "../../../hooks/useModalDialog";
import styles from "./ErrorAlert.module.css";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useEventTracking from "../../../hooks/useEventTracking";
import { mcn } from "../../../utils/mergeClassnames";
import { trackWithDefaultStage } from "@flights/et-universal";
import useRouteName from "../../../hooks/useRouteName";
import useFlightsWebCheckoutErrorRedirect from "../../../utils/experiments/funnel/flights_web_checkout_error_redirect";
import { OutletContext } from "../AppShell/Outlet";
import { useHistory } from "react-router-dom";
import useClientCachedURL from "../../../hooks/useClientCachedURL";

interface Props {
  illustration?: React.ReactNode;
  title: string;
  body?: string;
  buttonLabel: string;
  loading?: boolean;
  onClick: () => void;
  experimentButton?: { buttonLabel: string; onClick: () => void };
  children?: React.ReactNode;
  size?: "large" | number;
  variant?: "neutral" | "white";
}

export const ErrorAlertInner: React.FC<Props> = ({
  illustration,
  title,
  body,
  buttonLabel,
  loading = false,
  onClick,
  children,
  size = "large",
  variant = "neutral"
}) => {
  const Modal = useModalDialog();
  const { isMobile } = useUserAgent();
  const { isSmall } = useViewport();
  const isResponsiveFixesSm = isSmall && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  return (
    <Modal
      title=""
      subtitle=""
      closeAriaLabel="Close bottom sheet"
      active={true}
      fill={true}
      size={size}
      position={isMobile ? "fullScreen" : "center"}
      lockClose={true}
      lockCloseOnOutsideClick={false}
      hideClose={true}
      zIndex={500}
      className={isResponsiveFixesSm ? styles.modalSm : ""}
    >
      <Stack direction={"column"} justifyContent={"center"} alignItems={"center"}>
        <Stack.Item
          className={mcn(styles.illustrationContainer, variant === "white" && styles.illustrationContainer_white)}
        >
          {illustration || <NotAvailableIllustration />}
        </Stack.Item>
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          mixin={{ width: "100%", padding: 4 }}
        >
          <Stack.Item>
            <Text tagName="h1" variant="headline_2">
              {title}
            </Text>
          </Stack.Item>
          <Stack.Item>{body ? <Text variant="body_2">{body}</Text> : children}</Stack.Item>
          <Stack.Item className={isResponsiveFixesSm ? styles.buttonContainerSm : ""}>
            <Button
              className={mcn(styles.button, isResponsiveFixesSm && styles.buttonSm)}
              onClick={onClick}
              loading={loading}
              wide={true}
              loadingAriaLabel=""
            >
              {buttonLabel}
            </Button>
          </Stack.Item>
        </Stack>
      </Stack>
    </Modal>
  );
};

const ErrorAlert = (props: Props) => {
  const { title, onClick, ...rest } = props;
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const ariaLiveActions = useActions(AriaLiveActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const { visible } = useStore().loadingScreen;
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("page_load", requestId);

  useEffect(() => {
    trackV2("show_error_page");
  }, [trackV2]);

  useEffect(() => {
    if (visible && title) {
      ariaLiveActions.setMessage({
        message: title,
        type: "assertive"
      });
    } else {
      ariaLiveActions.reset();
    }
  }, [ariaLiveActions, visible, title]);

  // flights_web_checkout_error_redirect -- start
  // ================================================
  // eslint-disable-next-line
  const flights_web_checkout_error_redirect = useFlightsWebCheckoutErrorRedirect();
  const { setSheetContainer } = useContext(OutletContext);
  const history = useHistory();
  const routeName = useRouteName();
  const { getLastSuccessfulSearchUrl } = useClientCachedURL();

  const autoRedirectToSearchResult = useMemo(() => {
    if (
      ![
        "checkout-start",
        "checkout-ticket-type",
        "checkout-fare",
        "checkout-pax",
        "checkout-extras",
        "checkout-seat-selection",
        "checkout-sirf",
        "checkout-payment"
      ].includes(routeName)
    ) {
      return false;
    }
    return !!flights_web_checkout_error_redirect.track();
  }, [flights_web_checkout_error_redirect]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnClick = useCallback(() => {
    if (autoRedirectToSearchResult) {
      setSheetContainer(undefined);
    } else {
      onClick?.();
    }
  }, [onClick, autoRedirectToSearchResult, setSheetContainer]);
  // ================================================
  // flights_web_checkout_error_redirect -- end

  const errorAlert = useMemo(
    () => <ErrorAlertInner title={title} onClick={handleOnClick} {...rest} />,
    [rest, title, handleOnClick]
  );

  // flights_web_checkout_error_redirect -- start
  // ================================================
  useEffect(() => {
    if (!autoRedirectToSearchResult) return;
    setSheetContainer(errorAlert);
    setTimeout(() => {
      history.push(getLastSuccessfulSearchUrl(), { replace: true, flights_web_checkout_error_redirect: true });
    }, 2000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (autoRedirectToSearchResult) return null;
  // ================================================
  // flights_web_checkout_error_redirect -- end

  return errorAlert;
};

export default ErrorAlert;
